import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import { Callback } from '../../../modules/callback'
import MainModule from '../../../modules/main'
import { Font, config } from '../../../constants'

Locale.getInstance().registerKeySet('fr', 'create-contribution-select-type', {
  title: 'Définir le type de contribution',
  description: 'Veuillez choisir le type de contribution à associer à votre contributeur.',
  contributorNameLabel: 'Nom du contributeur*',
  contributorTypeLabel: 'Type de contributeur*',
  contributionTypeLabel: 'Type de contribution*',
  contributorTypesLabels: 'Non spécifié,Personne,Organisation',
})

Locale.getInstance().registerKeySet('en', 'create-contribution-select-type', {
  title: 'Define contribution type',
  description: 'Please choose the contribution type to associate with your contributor.',
  contributorNameLabel: 'Contributor name*',
  contributorTypeLabel: 'Contributor type*',
  contributionTypeLabel: 'Contribution type*',
  contributorTypesLabels: 'Not specified,Person,Organization',
})

const CONTRIBUTOR_TYPES_SELECT_VALUES = ['', 'Person', 'Organization'].join(',')

export interface LvcCreateContributionSelectType {
  flowGroup: string
  flowStep: string
  contributorName: string
  contributorType: string
  contributionType: string
  errors: { [key: string]: string }
}

export class LvcCreateContributionSelectType
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private flowCore: IFlowCore | undefined
  private onContributionTypeChangeCb: string = ''

  attrs: Attrs = [
    ['flow-step', ''],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['contributorName', ''],
    ['contributorType', ''],
    ['contributionType', ''],
    ['errors', {}],
  ]

  onReady() {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
    this.flowCore = mainModule.getFlowManager().getCore(this.flowGroup)
    this.onContributionTypeChangeCb = Callback.attach(this.onContributionTypeChange.bind(this))
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  onContributionTypeChange(value: string) {
    this.contributionType = value
    this.errors = {}
    this.rerender()
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // Get contributor data from the previous step (createContributionSearchOrAdd)
    const searchOrAddData = data?.createContributionSearchOrAdd
    if (searchOrAddData) {
      this.contributorName = searchOrAddData.contributorName || ''
      this.contributorType = searchOrAddData.contributorType || ''
    }

    // Get contribution type if previously selected
    if (stepData?.contributionType) {
      this.contributionType = stepData.contributionType
    }

    this.rerender()
  }

  public async serialize(): Promise<unknown> {
    return {
      contributionType: this.contributionType,
    }
  }

  public async validate(): Promise<boolean> {
    this.errors = {}
    let isValid = true

    if (!this.contributionType) {
      this.errors.contributionType = Locale.getKey('general.required')
      isValid = false
    }

    if (!isValid) {
      window.scrollTo(0, 0)
    }

    this.rerender()
    return isValid
  }

  getContributorTypeLabel(): string {
    const labels = Locale.getKey('create-contribution-select-type.contributorTypesLabels').split(
      ','
    )
    const values = CONTRIBUTOR_TYPES_SELECT_VALUES.split(',')
    const index = values.indexOf(this.contributorType)
    return index > 0 ? labels[index] : labels[0]
  }

  html = () => `
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey('create-contribution-select-type.title')}
      </lvc-text>
    </lvc-box>
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey('create-contribution-select-type.description')}
      </lvc-text>
    </lvc-box>
    <lvc-box
      mt="24px"
      background="white"
      padding="24px"
      borderRadius="8px"
      border="1px solid #1E007D4D"
    >
      <lvc-box mb="16px">
        <lvc-input
          name="contributorName"
          disabled="true"
          default-value="${this.contributorName}"
          label="${Locale.getKey('create-contribution-select-type.contributorNameLabel')}"
          secondary-style="true"
        ></lvc-input>
      </lvc-box>
      <lvc-box display="flex" columnGap="16px">
        <lvc-input
          name="contributorType"
          disabled="true"
          default-value="${this.getContributorTypeLabel()}"
          label="${Locale.getKey('create-contribution-select-type.contributorTypeLabel')}"
          secondary-style="true"
        ></lvc-input>
        <lvc-contribution-type-field-all
          title="${Locale.getKey('create-contribution-select-type.contributionTypeLabel')}"
          on-change="${this.onContributionTypeChangeCb}"
          default-value="${this.contributionType ? encodeURIComponent(JSON.stringify([{ id: this.contributionType }])) : ''}"
          error="${this.errors.contributionType || ''}"
        ></lvc-contribution-type-field-all>
      </lvc-box>
    </lvc-box>
  `

  css = () => `
    :host {
      display: block;
    }
  `

  eventListeners: EventListeners = []
}
